import { message, Form, Button, Drawer } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import { useState, useMemo, useRef } from 'react';
import { providerService } from 'services';
import FormItem from 'components/FormItem';
import { useAsync } from 'react-async-hook';
import BankingProfileForm from 'components/BankingProfileForm';
import { PROVIDER_APP_PAYMENT_METHODS } from 'containers/Provider';
import MaskedDisplay from 'components/ui/MaskedDisplay';
import { FileList, GetFileContentsEntityTypes } from 'components/FileList/FileList';
import Select from 'components/Select';

export function PaymentDetails({ application, careFacility, setCareFacility, isEditable = false }) {
  const [form] = Form.useForm();
  const [profileForm] = Form.useForm();
  const [orgProfiles, setOrgProfiles] = useState([]);

  const [showCreateDrawer, setShowCreateDrawer] = useState(false);
  const [creatingProfile, setCreatingProfile] = useState(false);
  const stagedVoidedCheckRef = useRef(null);

  // Fetch organization banking profiles
  useAsync(async () => {
    if (careFacility?.organization?.id !== undefined) {
      try {
        const profiles = await providerService.getBankingProfiles(careFacility.organization.id);
        setOrgProfiles(profiles || []);
      } catch (error) {
        console.log('Failed to fetch organization profiles', error);
      }
    }
  }, [careFacility?.organization?.id]);

  const { isEditing, editProps } = useEditCard({
    id: 'payment-details',
    onEditStart: () => {
      const activeProfile = orgProfiles.find((p) => p.id === careFacility?.banking?.id);
      form.setFieldsValue({
        bankingProfileId: activeProfile?.id || null,
      });
    },
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();

      if (!values.bankingProfileId) {
        message.error('Please select a banking profile');
        throw new Error('No banking profile selected');
      }

      // Activate the selected profile for this care facility
      await providerService.activateBankingProfile(careFacility.id, values.bankingProfileId);

      const updatedCareFacility = await providerService.getCenterById(Number(careFacility?.id));
      setCareFacility(updatedCareFacility);
      form.resetFields();
      message.success('Payment details updated');
    },
    onDiscard: () => form.resetFields(),
  });

  const selectedProfileId = Form.useWatch('bankingProfileId', form);
  const selectedProfile = useMemo(
    () => orgProfiles.find((p) => p.id === selectedProfileId),
    [orgProfiles, selectedProfileId],
  );

  const activeProfile = useMemo(
    () => orgProfiles.find((p) => p.id === careFacility?.banking?.id),
    [orgProfiles, careFacility?.banking?.id],
  );

  const handleCreateNewProfile = async (values) => {
    try {
      setCreatingProfile(true);

      const bankingDto = {
        profileName: values.profileName,
        paymentName: values.paymentName,
        directDeposit: values.paymentMethod === PROVIDER_APP_PAYMENT_METHODS.DIRECT_DEPOSIT,
        accountType: values.banking?.accountType || null,
        bankName: values.banking?.bankName || null,
        routingNumber: values.banking?.routingNumber || null,
        accountNumber: values.banking?.accountNumber || null,
      };

      const newProfile = await providerService.createBankingProfile(careFacility.organization.id, bankingDto);

      if (stagedVoidedCheckRef.current) {
        try {
          await providerService.uploadVoidedCheck(
            careFacility.organization.id,
            newProfile.id,
            stagedVoidedCheckRef.current,
          );
        } catch (err) {
          message.warning('Profile created but voided check upload failed. Please edit the profile to retry.');
          console.error('Voided check upload failed:', err);
        }
      }

      message.success('Banking profile created successfully');

      const profiles = await providerService.getBankingProfiles(careFacility.organization.id);
      setOrgProfiles(profiles || []);

      stagedVoidedCheckRef.current = null;
      setShowCreateDrawer(false);
      profileForm.resetFields();

      form.setFieldsValue({ bankingProfileId: newProfile.id });
    } catch (error) {
      console.error('Error creating banking profile:', error);
      message.error(error.response?.data?.message || 'Failed to create banking profile');
    } finally {
      setCreatingProfile(false);
    }
  };

  const profileOptions = useMemo(() => {
    return orgProfiles.map((profile) => ({
      id: profile.id,
      title: profile.profileName || 'Unnamed Profile',
      subtitle: `${profile.directDeposit ? 'Direct Deposit' : 'Paper Check'}${
        profile.paymentName ? ` • ${profile.paymentName}` : ''
      }`,
    }));
  }, [orgProfiles]);

  return (
    <>
      <Card
        className="mt-8"
        title="Payment details"
        extra={isEditable ? <CardEdit {...editProps} /> : null}
        noBodyPadding
        stickyHeader
      >
        <Form form={form} disabled={editProps.loading}>
          <TabularList
            indented
            data={[
              {
                title: 'Banking Profile',
                titleRequired: true,
                value: isEditing ? (
                  <div className="flex flex-col gap-2 max-w-lg">
                    <FormItem
                      name="bankingProfileId"
                      rules={[{ required: true, message: 'Banking profile is required' }]}
                      className="mb-0"
                    >
                      <Select
                        placeholder="Select a banking profile"
                        options={profileOptions}
                        formatOptionLabel={(opt, { context }) => {
                          if (context === 'value') {
                            return opt.title;
                          }

                          return (
                            <div>
                              <div>{opt.title}</div>
                              <div className="text-xs text-gray-500">{opt.subtitle}</div>
                            </div>
                          );
                        }}
                        isOptionSelected={(option, values) => {
                          const valueId = Array.isArray(values) ? values[0] : values;
                          return option.id === valueId;
                        }}
                        onChangeFormatter={(option) => option?.id}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </FormItem>

                    <Button
                      type="link"
                      onClick={() => {
                        stagedVoidedCheckRef.current = null;
                        profileForm.resetFields();
                        setShowCreateDrawer(true);
                      }}
                      className="p-0"
                      style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                    >
                      + Create New Profile
                    </Button>
                  </div>
                ) : activeProfile ? (
                  activeProfile.profileName || 'Unnamed Profile'
                ) : (
                  'No profile selected'
                ),
              },
              ...(activeProfile && !isEditing
                ? [
                    {
                      title: 'Payment method',
                      value: activeProfile.directDeposit ? 'Direct deposit' : 'Paper check',
                    },
                    {
                      title: 'Make check payable to',
                      value: activeProfile.paymentName,
                    },
                    ...(activeProfile.directDeposit
                      ? [
                          {
                            title: 'Account type',
                            value: activeProfile.accountType?.title || activeProfile.accountType?.label || '-',
                          },
                          {
                            title: 'Bank name',
                            value: activeProfile.bankName || '-',
                          },
                          {
                            title: 'Routing number',
                            value: activeProfile.routingNumber ? (
                              <MaskedDisplay value={activeProfile.routingNumber} />
                            ) : (
                              '-'
                            ),
                          },
                          {
                            title: 'Account number',
                            value: activeProfile.accountNumber ? (
                              <MaskedDisplay value={activeProfile.accountNumber} />
                            ) : (
                              '-'
                            ),
                          },
                        ]
                      : []),
                    {
                      title: 'Voided check',
                      value: activeProfile.voidedCheckFileId ? (
                        <FileList
                          entityType={GetFileContentsEntityTypes.PROVIDER_ORGANIZATION}
                          organizationId={careFacility?.organization?.id}
                          files={[
                            {
                              id: activeProfile.voidedCheckFileId,
                              name: activeProfile.voidedCheckFileName ?? 'Voided check',
                            },
                          ]}
                        />
                      ) : (
                        '-'
                      ),
                    },
                  ]
                : []),
              ...(selectedProfile && isEditing
                ? [
                    {
                      title: 'Payment method',
                      value: selectedProfile?.directDeposit ? 'Direct deposit' : 'Paper check',
                    },
                    {
                      title: 'Make check payable to',
                      value: selectedProfile?.paymentName,
                    },
                    ...(selectedProfile?.directDeposit
                      ? [
                          {
                            title: 'Account type',
                            value: selectedProfile?.accountType?.title || selectedProfile?.accountType?.label || '-',
                          },
                          {
                            title: 'Bank name',
                            value: selectedProfile?.bankName || '-',
                          },
                          {
                            title: 'Routing number',
                            value: selectedProfile?.routingNumber ? (
                              <MaskedDisplay value={selectedProfile.routingNumber} />
                            ) : (
                              '-'
                            ),
                          },
                          {
                            title: 'Account number',
                            value: selectedProfile?.accountNumber ? (
                              <MaskedDisplay value={selectedProfile.accountNumber} />
                            ) : (
                              '-'
                            ),
                          },
                        ]
                      : []),
                    {
                      title: 'Voided check',
                      value: selectedProfile?.voidedCheckFileId ? (
                        <FileList
                          entityType={GetFileContentsEntityTypes.PROVIDER_ORGANIZATION}
                          organizationId={careFacility?.organization?.id}
                          files={[
                            {
                              id: selectedProfile.voidedCheckFileId,
                              name: selectedProfile.voidedCheckFileName ?? 'Voided check',
                            },
                          ]}
                        />
                      ) : (
                        '-'
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Form>
      </Card>

      <Drawer
        title="Create New Banking Profile"
        placement="right"
        onClose={() => {
          stagedVoidedCheckRef.current = null;
          setShowCreateDrawer(false);
          profileForm.resetFields();
        }}
        open={showCreateDrawer}
        width={640}
        footer={
          <div className="flex justify-end gap-3">
            <Button
              size="large"
              onClick={() => {
                stagedVoidedCheckRef.current = null;
                setShowCreateDrawer(false);
                profileForm.resetFields();
              }}
              disabled={creatingProfile}
            >
              Cancel
            </Button>
            <Button type="primary" size="large" onClick={() => profileForm.submit()} loading={creatingProfile}>
              Save profile
            </Button>
          </div>
        }
      >
        <Form form={profileForm} onFinish={handleCreateNewProfile} layout="vertical">
          <BankingProfileForm
            form={profileForm}
            orgId={careFacility?.organization?.id}
            onVoidedCheckStaged={(file) => {
              stagedVoidedCheckRef.current = file;
            }}
          />
        </Form>
      </Drawer>
    </>
  );
}
